import {
  DEFAULT_TEXT_FORMAT,
  TEXT_FORMAT_TOOL_SETS,
  TEXT_FORMAT_TOOLS,
} from "../../../../components/shared/textToolbar/textFormatModel";
import {
  DEFAULT_WATERMARK_SETTINGS,
  WATERMARK_CONTENT_TYPES,
  applyWatermarkPosition,
  applyWatermarkTextCase,
  getWatermarkFontsForType,
} from "../Popup/watermarkSettings";

export function watermarkToTextFormat(settings = {}) {
  const contentType = settings.contentType || WATERMARK_CONTENT_TYPES.TEXT;
  const underline = Boolean(settings.underline);
  const strikethrough = Boolean(settings.strikethrough);

  return {
    ...DEFAULT_TEXT_FORMAT,
    fontId: settings.fontId || DEFAULT_TEXT_FORMAT.fontId,
    size: Number(settings.size) || DEFAULT_TEXT_FORMAT.size,
    color: settings.color || DEFAULT_TEXT_FORMAT.color,
    fontWeight: settings.fontWeight === "bold" ? "bold" : "normal",
    fontStyle: settings.fontStyle === "italic" ? "italic" : "normal",
    underline,
    strikethrough,
    textAlign: settings.textAlign || DEFAULT_WATERMARK_SETTINGS.textAlign,
    letterSpacing: Number(settings.letterSpacing ?? DEFAULT_TEXT_FORMAT.letterSpacing),
    lineHeight: Number(settings.lineHeight ?? DEFAULT_TEXT_FORMAT.lineHeight),
    textVerticalAlign:
      settings.textVerticalAlign || DEFAULT_TEXT_FORMAT.textVerticalAlign,
    textScript: settings.textScript || DEFAULT_TEXT_FORMAT.textScript,
    effect: settings.effect || DEFAULT_TEXT_FORMAT.effect,
    opacity: Number(settings.opacity ?? DEFAULT_TEXT_FORMAT.opacity),
    position: settings.position || DEFAULT_TEXT_FORMAT.position,
    offsetX: Number(settings.offsetX) || 0,
    offsetY: Number(settings.offsetY) || 0,
    rotation: Number(settings.rotation) || 0,
    spacing: Number(settings.spacing ?? 0.5),
    margin: Number.isFinite(Number(settings.margin))
      ? Math.max(0, Math.min(0.4, Number(settings.margin)))
      : 0.08,
    tile: settings.tile || "single",
    lockAspectRatio: settings.lockAspectRatio !== false,
    _meta: {
      contentType,
      text: settings.text || "",
      tile: settings.tile || "single",
      markWidth: settings.markWidth,
      markHeight: settings.markHeight,
      margin: Number.isFinite(Number(settings.margin))
        ? Math.max(0, Math.min(0.4, Number(settings.margin)))
        : 0.08,
      flushEdges: settings.flushEdges !== false,
      logoNaturalWidth: Number(settings.logoNaturalWidth) || 0,
      logoNaturalHeight: Number(settings.logoNaturalHeight) || 0,
    },
  };
}

export function textFormatPatchToWatermark(patch, settings = {}) {
  const next = { ...settings };
  if (patch.fontId !== undefined) next.fontId = patch.fontId;
  if (patch.size !== undefined) next.size = patch.size;
  if (patch.color !== undefined) next.color = patch.color;
  if (patch.fontWeight !== undefined) next.fontWeight = patch.fontWeight;
  if (patch.fontStyle !== undefined) next.fontStyle = patch.fontStyle;
  if (patch.underline !== undefined) next.underline = patch.underline;
  if (patch.strikethrough !== undefined) next.strikethrough = patch.strikethrough;
  if (patch.textAlign !== undefined) next.textAlign = patch.textAlign;
  if (patch.letterSpacing !== undefined) {
    next.letterSpacing = Math.max(
      -100,
      Math.min(200, Number(patch.letterSpacing) || 0)
    );
  }
  if (patch.lineHeight !== undefined) {
    next.lineHeight = Math.max(
      0.5,
      Math.min(3, Number(patch.lineHeight) || 1.4)
    );
  }
  if (patch.textVerticalAlign !== undefined) {
    next.textVerticalAlign = ["top", "middle", "bottom"].includes(
      patch.textVerticalAlign
    )
      ? patch.textVerticalAlign
      : "top";
  }
  if (patch.textScript !== undefined) {
    next.textScript = ["normal", "super", "sub"].includes(patch.textScript)
      ? patch.textScript
      : "normal";
  }
  if (patch.effect !== undefined) {
    next.effect = ["none", "shadow", "outline", "glow"].includes(patch.effect)
      ? patch.effect
      : "none";
  }
  if (patch.opacity !== undefined) next.opacity = patch.opacity;
  if (patch.position !== undefined && patch.position !== settings.position) {
    let placed = applyWatermarkPosition(
      { ...next, position: patch.position },
      patch.position
    );
    if (patch.margin !== undefined) {
      placed = {
        ...placed,
        margin: Math.max(0, Math.min(0.4, Number(patch.margin) || 0)),
      };
    }
    if (patch.offsetX !== undefined) placed = { ...placed, offsetX: patch.offsetX };
    if (patch.offsetY !== undefined) placed = { ...placed, offsetY: patch.offsetY };
    return placed;
  }
  if (patch.margin !== undefined) {
    next.margin = Math.max(0, Math.min(0.4, Number(patch.margin) || 0));
    // Only reset offsets when the caller did not supply new ones
    // (nearest-edge margin moves via offsetX/Y).
    if (patch.offsetX === undefined) next.offsetX = 0;
    if (patch.offsetY === undefined) next.offsetY = 0;
  }
  if (patch.offsetX !== undefined) next.offsetX = patch.offsetX;
  if (patch.offsetY !== undefined) next.offsetY = patch.offsetY;
  if (patch.rotation !== undefined) {
    next.rotation = Math.max(-180, Math.min(180, Number(patch.rotation) || 0));
  }
  if (patch.spacing !== undefined) {
    next.spacing = Math.max(0, Math.min(3.96, Number(patch.spacing) || 0));
  }
  if (patch.lockAspectRatio !== undefined) {
    next.lockAspectRatio = Boolean(patch.lockAspectRatio);
  }
  if (patch.textCase !== undefined && patch.text !== undefined) {
    next.text = applyWatermarkTextCase(patch.text, patch.textCase);
    return next;
  }
  return next;
}

export function getWatermarkTextFormatTools(settings = {}) {
  const contentType = settings.contentType || WATERMARK_CONTENT_TYPES.TEXT;
  const isLogo =
    contentType === WATERMARK_CONTENT_TYPES.LOGO && Boolean(settings.logoUrl);
  // Logo uses ImageFormatToolbar — text toolbar tools stay empty here.
  if (isLogo) return [];
  return TEXT_FORMAT_TOOL_SETS.editorWatermarkText;
}

export function getWatermarkTextFormatFonts(settings = {}) {
  const contentType = settings.contentType || WATERMARK_CONTENT_TYPES.TEXT;
  return getWatermarkFontsForType(contentType);
}
